import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { roleService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';

const RoleManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/roles');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('roleManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
  }, ['/roles']);
  const { currentPage, itemsPerPage, searchTerm } = filters;
  const setCurrentPage  = (v) => setFilters({ currentPage: v });
  const setItemsPerPage = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm   = (v) => setFilters({ searchTerm: v });

  useEffect(() => {
    setPageTitle('Roles');
  }, [setPageTitle]);

  useEffect(() => {
    fetchRoles();
  }, [currentPage, searchTerm, itemsPerPage]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;

      const response = await roleService.getAll(params);
      setRoles(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch roles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleView = (role) => {
    navigate(`/roles/view/${role.id}`);
  };

  const handleEdit = (role) => {
    navigate(`/roles/edit/${role.id}`);
  };

  const handleAddNew = () => {
    navigate('/roles/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this role?');
    if (confirmed) {
      try {
        await roleService.delete(id);
        toast.error('Role deleted successfully');
        fetchRoles();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete role');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (role, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [role.id]: true }));
    try {
      await roleService.updateStatus(role.id, newStatus);
      toast.success(`Role ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchRoles();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update role status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [role.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          addButton={permissions.can_add ? {
            label: 'Add Role',
            onClick: handleAddNew,
            variant: 'primary'
          } : null}
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search roles...'
            }
          }}
        />

        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
          columns={[
            {
              header: 'S.No',
              accessor: 'serial',
              width: '80px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            {
              header: 'Code',
              accessor: 'role_code',
              width: '120px',
              render: (value) => (
                <span style={{
                  fontFamily: 'monospace',
                  backgroundColor: 'var(--gray-100)',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.875rem'
                }}>
                  {value}
                </span>
              )
            },
            {
              header: 'Role Name',
              accessor: 'role_name',
              width: '220px',
              render: (value) => (
                <span className="text-sm text-gray-900" title={value}>
                  {value && value.length > 30 ? `${value.substring(0, 30)}...` : value}
                </span>
              )
            },
            {
              header: 'Created',
              accessor: 'created_at',
              width: '120px',
              render: (value) => formatDateDDMMYYYY(value),
            },
            {
              header: 'Status',
              accessor: 'is_active',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle
                  status={value}
                  onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                  loading={updatingStatus[row.id]}
                  disabled={!permissions.can_edit}
                />
              )
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '120px',
              align: 'center',
              actions: [
                ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
              ]
            },
          ]}
          data={permissions.can_list ? roles : []}
          onRowClick={permissions.can_view ? handleView : undefined}
          emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No roles found'}
          loading={loading}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Card>
    </div>
  );
};

export default RoleManagement;
